
import { Coordinates, HikingTrail, RouteType } from "../types";
import { getDistance } from "./geometryUtils";
import { MAP_LAYERS } from "../constants";
import { robustOverpassQuery } from "./overpassService";
import { detectCountry } from "../utils/countryDetection";
import { fetchGeoNorgeNearbyRoutes } from "./geonorgeRoutesService";
import i18n from "../i18n";

export let lastHikingDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  wmsUrl: MAP_LAYERS.GEONORGE_HIKING_WMS,
  queryCount: 0,
  timestamp: 0
};

export const fetchNearbyHikingTrails = async (coords: Coordinates): Promise<HikingTrail[]> => {
  if (!coords || isNaN(coords.latitude) || isNaN(coords.longitude)) {
    console.warn("fetchNearbyHikingTrails: Ugyldige koordinater");
    return [];
  }

  // In Norway, use Kartverket's authoritative route database (typed: foot/ski/bike/other).
  // Fall back to OSM if the WFS returns nothing (gap areas) or for spots outside Norway.
  if (detectCountry(coords.latitude, coords.longitude) === 'NO') {
    try {
      const geonorge = await fetchGeoNorgeNearbyRoutes(coords);
      if (geonorge.length > 0) return geonorge;
    } catch { /* fall through to OSM */ }
  }

  // Øker radius noe for å fange opp stier som starter litt lenger unna, men som er relevante
  const radius = 2000;
  const lat = coords.latitude;
  const lon = coords.longitude;

  // Utvidet spørring for å inkludere relasjoner (langdistanseruter) og flere stimerker
  const query = `[out:json][timeout:12];
    (
      way["highway"~"path|footway|track"](around:${radius}, ${lat}, ${lon});
      way["hiking"="yes"](around:${radius}, ${lat}, ${lon});
      way["route"="hiking"](around:${radius}, ${lat}, ${lon});
      way["trailblazed"](around:${radius}, ${lat}, ${lon});
      way["sac_scale"](around:${radius}, ${lat}, ${lon});
      rel["route"="hiking"](around:${radius}, ${lat}, ${lon});
    );
    out geom;`;

  try {
    const data = await robustOverpassQuery(query, lastHikingDebug);
    return parseHikingTrails(data, lat, lon);
  } catch (error: any) {
    lastHikingDebug.lastError = error?.message;
    console.debug(`[hikingService] Overpass query failed: ${error?.message}`);
    return [];
  }
};

/** Map OSM tags to our normalized route category (matches GeoNorge's foot/ski/bike/other). */
const osmRouteType = (tags: Record<string, string>): RouteType => {
  const route = tags.route;
  if (route === 'bicycle' || route === 'mtb' || tags.bicycle === 'yes') return 'bike';
  if (route === 'ski' || route === 'piste' || tags['piste:type']) return 'ski';
  if (route === 'hiking' || route === 'foot' || tags.hiking === 'yes' ||
      tags.highway === 'path' || tags.highway === 'footway' || tags.sac_scale) return 'foot';
  return 'other';
};

const parseHikingTrails = (data: any, lat: number, lon: number): HikingTrail[] => {
  if (!data?.elements || data.elements.length === 0) {
    lastHikingDebug.queryCount = 0;
    return [];
  }

  lastHikingDebug.queryCount = data.elements.length;
  const trails: HikingTrail[] = [];

  data.elements.forEach((e: any) => {
    if ((e.type !== 'way' && e.type !== 'relation') || !e.geometry) return;

    const geometry: Coordinates[] = e.geometry.map((p: any) => ({
      latitude: p.lat,
      longitude: p.lon
    }));
    if (geometry.length < 2) return;

    const tags = e.tags || {};
    const name = tags.name || tags.ref;
    const isHikingRelated = tags.hiking === 'yes' ||
      tags.route === 'hiking' ||
      tags.sac_scale ||
      tags.trailblazed ||
      tags.highway === 'path';
    if (!name && !isHikingRelated) return;

    let totalLength = 0;
    for (let i = 0; i < geometry.length - 1; i++) {
      totalLength += getDistance(
        geometry[i].latitude, geometry[i].longitude,
        geometry[i + 1].latitude, geometry[i + 1].longitude
      );
    }
    if (totalLength < 2000) return;

    const distStart = getDistance(lat, lon, geometry[0].latitude, geometry[0].longitude);
    const distEnd = getDistance(lat, lon, geometry[geometry.length - 1].latitude, geometry[geometry.length - 1].longitude);
    const hours = totalLength / 1000 / 3.5;
    const totalMinutes = Math.round(hours * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const estimatedTime = hrs > 0
      ? `${hrs}t ${mins > 0 ? `${mins}min` : ''}`
      : `${mins}min`;

    trails.push({
      id: `trail-${e.type}-${e.id}`,
      name: name || (tags.sac_scale ? i18n.t('route.hiking.trail_with_grade', { grade: tags.sac_scale }) : i18n.t('route.hiking.unnamed_trail')),
      type: tags.highway || (e.type === 'relation' ? i18n.t('route.hiking.type_route') : i18n.t('route.hiking.type_trail')),
      routeType: osmRouteType(tags),
      marked: !!tags.trailblazed || !!tags.osmc_symbol,
      distanceToStart: Math.min(distStart, distEnd),
      geometry,
      totalLength,
      estimatedTime,
      difficulty: tags.sac_scale || tags.difficulty,
      symbol: tags.osmc_symbol,
      externalUrl:
        tags.website ||
        tags.url ||
        (typeof tags.wikipedia === 'string'
          ? `https://no.wikipedia.org/wiki/${encodeURIComponent(String(tags.wikipedia).replace(/^no:/, ''))}`
          : undefined),
      externalLabel:
        tags.website || tags.url
          ? i18n.t('route.hiking.external_link')
          : (typeof tags.wikipedia === 'string' ? i18n.t('route.hiking.wikipedia') : undefined)
    });
  });

  return trails
    .filter((v, i, a) => a.findIndex(t => t.name === v.name && Math.abs(t.distanceToStart - v.distanceToStart) < 100) === i)
    .sort((a, b) => a.distanceToStart - b.distanceToStart)
    .slice(0, 20);
};
